import { cutTargetToScene } from '@ps/views/interactive-video/timeline/utiles/segment-cut'
import { executeAction } from '@ps/workbench/undo-redo'
import { IvSegment, IvTrack } from './types'
import { isSegmentVideo } from './utiles'
import { resetCursorToScoped } from './current-frame'
import { useRemovedSegments } from './utiles/track-removed-segments'
import nodePool from '@ps/bridge/qc/node-pool'
import { getTimelineBySceneUuid } from './timeline'
import { pushIVRecordAction } from '@ps/store/interactive-video/state-action'
import {
  TrackGroupInsertResult,
  useTrackGroupInsertResultsByDiff,
} from './utiles/track-group-insert'
import store from '@ps/store'
import { findSceneSegment, updateSegmentToScene } from './utiles/scene'
import qc from 'qc'
import {
  getSceneNodeByUuid,
  isIvHotAreaNode,
  isVideo,
} from '@ps/bridge/qc/base'
import { IvTrackType } from './enums'
import { isIvSceneLinkNode } from '../brain/brain'
import { nodeTypes } from '@ps/bridge/qc/scripts/editor'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import { ivGroupNodePos } from '../common/utils'

export function pushTimelineCutRecordAction(
  mergedTarget: IvSegment,
  mergedTracks: IvTrack[],
): void {
  const beforeTracks = getTimelineBySceneUuid(mergedTarget.sceneUuid)
  const removedSegments = useRemovedSegments(mergedTarget.sceneUuid, () => {
    cutTargetToScene(mergedTarget, mergedTracks)
  })
  const changedTracks = getTimelineBySceneUuid(mergedTarget.sceneUuid)

  const videoSegments =
    mergedTracks
      .find((t) => t.type === IvTrackType.Video)
      ?.segments.sort((a, b) => a.start - b.start) ?? []
  const newTarget = videoSegments.find((s) => s.id === mergedTarget.id)

  if (newTarget && isSegmentVideo(newTarget)) {
    const start = videoSegments
      .slice(
        0,
        videoSegments.findIndex((s) => s.id === newTarget.id),
      )
      .reduce((start, s) => (start += s.frames), 0)

    resetCursorToScoped({ ...newTarget, start })
  }

  const nodes = removedSegments
    .map((uuid) => nodePool.find(uuid))
    .filter((node) => Boolean(node))

  const groupInsertResults = useTrackGroupInsertResultsByDiff(
    mergedTarget.sceneUuid,
    changedTracks,
    beforeTracks,
  )

  timelineCut2NodeAction(groupInsertResults, nodes)
}

export type removeNodeInfo = {
  node: qc.Node
  parent: qc.Node
  index: number
}
export type segmentGroupValue = {
  newValue: {
    groupInsertResults: TrackGroupInsertResult[]
  }
  oldValue: {
    groupInsertResults: TrackGroupInsertResult[]
    oldNodeData: removeNodeInfo[]
  }
}

type timelineCut2NodeValue = {
  newValue: {
    groupInsertResults: TrackGroupInsertResult[]
    removeNodes: qc.Node[]
  }
  oldValue: {
    groupInsertResults: TrackGroupInsertResult[]
    oldNodeData: removeNodeInfo[]
    removeNodesData: removeNodeInfo[]
  }
}

function getTimelineCut2NodeAction(value: timelineCut2NodeValue) {
  return {
    // eslint-disable-next-line
    execute: ({ newValue }: timelineCut2NodeValue) => {
      const groupInsertResults = newValue.groupInsertResults
      const removeNodes = newValue.removeNodes
      ivTimeline2NodeExecuteOperation(groupInsertResults)
      removeNodes.forEach((item) => {
        item.parent.removeChild(item)
      })
    },
    // eslint-disable-next-line
    undo: ({ oldValue }: timelineCut2NodeValue) => {
      const groupInsertResults = oldValue.groupInsertResults
      const oldNodeData = oldValue.oldNodeData
      const removeNodesData = oldValue.removeNodesData
      ivTimeline2NodeUndoOperation(groupInsertResults, oldNodeData)
      removeNodesData.forEach((item) => {
        item.parent.addChildAt(item.node, item.index)
      })
    },
    value,
    async: true,
    methodName: 'getTimelineCut2NodeAction',
  }
}

export function timelineCut2NodeAction(
  groupInsertResults: TrackGroupInsertResult[],
  removeNodes: qc.Node[],
) {
  const oldNodeData = getOldNodeData(groupInsertResults)
  const removeNodesData = removeNodes.map((item) => {
    return {
      node: item,
      parent: item.parent,
      index: item.parent.getChildIndex(item),
    }
  })
  executeAction({
    action: getTimelineCut2NodeAction({
      newValue: { groupInsertResults, removeNodes },
      oldValue: { groupInsertResults, oldNodeData, removeNodesData },
    }),
    record: true,
  })
}

export function ivTimeline2NodeExecuteOperation(
  groupInsertResults: TrackGroupInsertResult[],
) {
  const gameOb = store.state.workspace.game.data.gameOb
  groupInsertResults.forEach(async (item: TrackGroupInsertResult) => {
    const node = nodePool.find(item.nodeUuid)
    // link node,视频,互动热区不用调整group
    if (isIvSceneLinkNode(node) || isVideo(node) || isIvHotAreaNode(node)) {
      return
    }
    const sceneNode = getSceneNodeByUuid(item.nodeUuid)
    const segment = findSceneSegment(sceneNode.uuid, item.nodeUuid)
    const toGroupNode = nodePool.find(item.to)
    if (item.position === 'middle') {
      if (!node.parnet || node.parnet.uuid !== toGroupNode.uuid) {
        node.switchParent(toGroupNode, toGroupNode.children.length)
      }
    } else {
      let index
      if (item.position === 'top') {
        index = toGroupNode.parent.getChildIndex(toGroupNode) + 1
      } else {
        index = toGroupNode.parent.getChildIndex(toGroupNode)
      }
      const groupNode = gameOb.add.node(sceneNode)
      groupNode.name = 'group'
      const ped = editorScript.getScript(groupNode)
      ped.$data.nodeType = nodeTypes.ivGroup
      ivGroupNodePos(groupNode)
      const layout = groupNode.getScript('ps.Layout')
      if (layout) {
        groupNode.removeScript(layout)
      }
      sceneNode.setChildIndex(groupNode, index)
      node.switchParent(groupNode, 0)
      updateSegmentToScene(sceneNode.uuid, segment.id, {
        groupId: groupNode.uuid,
      })
    }
  })
}

export function getOldNodeData(
  groupInsertResults: TrackGroupInsertResult[],
): removeNodeInfo[] {
  const oldNodeData = groupInsertResults.map((item) => {
    const node = nodePool.find(item.nodeUuid)
    const parent = node.parent
    const index = parent.getChildIndex(node)
    return {
      node,
      parent,
      index,
    }
  })
  return oldNodeData
}

export function ivTimeline2NodeUndoOperation(
  groupInsertResults: TrackGroupInsertResult[],
  oldNodeData: removeNodeInfo[],
) {
  oldNodeData.forEach((item) => {
    item.node.switchParent(item.parent, item.index)
  })
  groupInsertResults.forEach(async (item: TrackGroupInsertResult) => {
    const toGroupNode = nodePool.find(item.to)
    const index = toGroupNode.parent.getChildIndex(toGroupNode)
    if (item.position === 'top') {
      toGroupNode.parent.removeChildAt(index + 1)
    } else if (item.position === 'bottom') {
      toGroupNode.parent.removeChildAt(index - 1)
    }
  })
}

function getSegmentDrag2NodeAction(value: segmentGroupValue) {
  return {
    // eslint-disable-next-line
    execute: ({ newValue }: segmentGroupValue) => {
      const groupInsertResults = newValue.groupInsertResults
      ivTimeline2NodeExecuteOperation(groupInsertResults)
    },
    // eslint-disable-next-line
    undo: ({ oldValue }: segmentGroupValue) => {
      const groupInsertResults = oldValue.groupInsertResults
      const oldNodeData = oldValue.oldNodeData
      ivTimeline2NodeUndoOperation(groupInsertResults, oldNodeData)
    },
    value,
    async: true,
    methodName: 'getSegmentDrag2NodeAction',
  }
}
export function segmentDrag2NodeAction(
  groupInsertResults: TrackGroupInsertResult[],
) {
  const oldNodeData = getOldNodeData(groupInsertResults)
  executeAction({
    action: getSegmentDrag2NodeAction({
      newValue: { groupInsertResults },
      oldValue: { groupInsertResults, oldNodeData },
    }),
    record: true,
  })
}

export function pushTimelineRecordAction(): void {
  pushIVRecordAction()
}
